import { DragEvent, useCallback, useEffect, useState } from 'react';
import { CoreBox, CoreDialog, CoreTypography } from '@youscience/core';
import { FormControl, FormHelperText, IconButton, AlertTitle, CircularProgress } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { PDFDocument } from 'pdf-lib';

import ActionButton from '@components/ActionButton';
import DialogFooter from '@components/DialogFooter';

import useNotificationStore from '@store/NotificationStore';

import UploadIcon from '@images/fileUpload.svg?react';
import UploadIcon2 from '@images/upload-file.svg?react';
import DeleteIcon from '@images/trash-button.svg?react';
import DeleteIcon2 from '@images/remove-red.svg?react';

import { PDF_FILE_PREFIX } from '../constants';
import ReviewPdf, { ReviewTypes } from '../ReviewPdf';

import { sxStyles, StyledAlert, StyledDragAndDropZone } from './FileUpload.styles';
import { UploadedFileWithMetadata } from '@youscience/commonapp-service-common';
import fileConversionService, { SUPPORTED_FILE_TYPES } from '@services/FileConversion';

export const MAX_SIZE_KB = 2000;

const handleDownload = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

interface FileUploadProps {
  title: string;
  name: string;
  initialFile?: string | null;
  previewTitle?: string;
  filename?: string | undefined;
  onFileUpload: (field: string) => (file: string) => void;
  onFileDelete: (field: string) => (file: undefined) => void;
  onFilePreview?: (field: string) => () => void;
  error?: string;
  maxFiles?: number;
  initialFiles?: UploadedFileWithMetadata[];
  onOriginalFilesChange?: (files: UploadedFileWithMetadata[]) => void;
}

const mergeFiles = async (files: UploadedFileWithMetadata[]): Promise<UploadedFileWithMetadata | undefined> => {
  const mergedPdf = await PDFDocument.create();

  for (const file of files) {
    if (!file.data) return;
    try {
      const pdf = await PDFDocument.load(file.data, { updateMetadata: false });
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => {
        mergedPdf.addPage(page);
      });
    } catch (error) {
      return;
    }
  }

  const mergedPdfFile = await mergedPdf.save();

  return await readAsDataURL(
    new File([new Uint8Array(mergedPdfFile.buffer as ArrayBuffer)], 'uploads.pdf', { type: 'application/pdf' }),
  );
};

const readAsDataURL = (file: File): Promise<UploadedFileWithMetadata | undefined> => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = function () {
      return resolve({ data: fileReader.result as ArrayBuffer, name: file.name, size: file.size, type: file.type });
    };
    fileReader.onerror = function () {
      return resolve(undefined);
    };
    fileReader.readAsDataURL(file);
  });
};

interface FileState {
  file?: string;
  uploadedFilename?: string;
  files: UploadedFileWithMetadata[];
}

interface UIState {
  isUploadOpen: boolean;
  isShowAlert: boolean;
  dragActive: boolean;
  isValidDrag: boolean;
  isPreviewOpen: boolean;
  isLoading: boolean;
}

export const FileUpload = (props: FileUploadProps) => {
  const { notify } = useNotificationStore((state) => state);
  const {
    title,
    name,
    filename,
    initialFile,
    previewTitle,
    onFileUpload,
    onFileDelete,
    error,
    maxFiles,
    initialFiles,
    onOriginalFilesChange,
  } = props;
  const [uiState, setUIState] = useState<UIState>({
    isUploadOpen: false,
    isShowAlert: false,
    dragActive: false,
    isValidDrag: false,
    isPreviewOpen: false,
    isLoading: false,
  });
  const [fileState, setFileState] = useState<FileState>({
    file: initialFile || undefined,
    uploadedFilename: undefined,
    files: initialFiles || [],
  });
  const [processingFiles, setProcessingFiles] = useState<File[]>([]);

  const multipleFiles = maxFiles && maxFiles > 1;

  useEffect(() => {
    if (uiState.isUploadOpen) {
      setUIState((prev) => ({ ...prev, isShowAlert: false }));
    }
  }, [uiState.isUploadOpen]);

  const processFile = async (file: File): Promise<UploadedFileWithMetadata | undefined> => {
    try {
      if (file.type !== 'application/pdf') {
        const convertedFile = await fileConversionService.convertToPDF(file);

        if (!convertedFile) {
          notify({
            title: 'Conversion Failed',
            message: `Failed to convert ${file.name} to PDF`,
            severity: 'error',
          });

          return undefined;
        }

        return await readAsDataURL(convertedFile);
      }

      return await readAsDataURL(file);
    } catch (error) {
      notify({
        title: 'Processing Error',
        message: `Failed to process ${file.name}`,
        severity: 'error',
      });

      return undefined;
    }
  };

  const readFiles = async (newFiles: File[]) => {
    if (newFiles.length + fileState.files.length > (maxFiles || 1)) {
      notify({
        title: 'File limit reached',
        message: `You can only upload up to ${maxFiles || 1} files`,
        severity: 'error',
      });
      return;
    }

    setUIState((prev) => ({ ...prev, isLoading: true }));
    setProcessingFiles(newFiles);
    try {
      const processedFiles = await Promise.all(newFiles.map(processFile));

      setFileState((prev) => {
        const newState = {
          ...prev,
          files: [...prev.files, ...processedFiles.filter((file): file is UploadedFileWithMetadata => !!file)],
        };

        return newState;
      });
    } finally {
      setUIState((prev) => ({ ...prev, isLoading: false }));
      setProcessingFiles([]);
    }
  };

  useEffect(() => {
    processFiles();
  }, [fileState.files]);

  const processFiles = async () => {
    if (fileState.files.length === 0) {
      return;
    }

    const mergedFile = await mergeFiles(fileState.files);

    if (!mergedFile) {
      setUIState((prev) => ({ ...prev, isShowAlert: true }));
      setFileState({
        ...fileState,
        files: [],
      });
    } else {
      setUIState((prev) => ({ ...prev, isShowAlert: false }));
      if (mergedFile.size > MAX_SIZE_KB * 1024) {
        notify({
          title: 'File too large',
          message: `The combined file size exceeds ${MAX_SIZE_KB}KB`,
          severity: 'error',
        });
        setFileState({
          ...fileState,
          files: [],
        });
      } else {
        setFileState((prev) => {
          const newState = {
            ...prev,
            uploadedFilename: prev.files.length === 1 ? prev.files[0].name : 'uploads.pdf',
            file: mergedFile.data.toString().split(PDF_FILE_PREFIX)[1] || '',
          };

          return newState;
        });

        notify({
          title: 'Files uploaded',
          message:
            fileState.files.length === 1
              ? 'File uploaded successfully'
              : `${fileState.files.length} files uploaded successfully`,
          severity: 'success',
        });
      }
    }
  };

  const onDragEnter = (e: DragEvent) => {
    e.nativeEvent.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      const items = e.dataTransfer?.items;
      if (!items) return;

      const allFiles = Array.from(items);
      const hasInvalidFile = allFiles.some(
        (item) => item.kind === 'file' && !SUPPORTED_FILE_TYPES.some((type) => type.mimeType === item.type),
      );

      setUIState((prev) => ({
        ...prev,
        dragActive: true,
        isValidDrag: !hasInvalidFile && allFiles.length <= (maxFiles || 1),
      }));
    }
  };

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setUIState((prev) => ({ ...prev, dragActive: false }));
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    readFiles(acceptedFiles);
    setUIState((prev) => ({
      ...prev,
      dragActive: false,
      isValidDrag: true,
    }));
  }, []);

  const onRemoveFile = (index: number) => {
    setFileState((prev) => {
      const newState = {
        ...prev,
        files: prev.files.filter((_, i) => i !== index),
      };

      return newState;
    });
    if (fileState.files.length === 1) {
      setFileState((prev) => {
        const newState = { ...prev, file: undefined };

        return newState;
      });
    }
  };

  const onRemoveAllFiles = () => {
    onFileDelete(name)(undefined);
    setFileState({
      ...fileState,
      file: undefined,
      files: [],
    });
  };

  const handleOnContinue = () => {
    if (uiState.isUploadOpen && fileState.file) {
      onFileUpload(name)(fileState.file);
      if (multipleFiles && fileState.files && onOriginalFilesChange) onOriginalFilesChange(fileState.files);
    }
    setUIState((prev) => ({ ...prev, isUploadOpen: false }));
  };

  const handleOnCancel = () => {
    setFileState({
      ...fileState,
      file: initialFile || undefined,
      files: initialFiles || [],
    });
    setUIState((prev) => ({ ...prev, isUploadOpen: false }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    accept: Object.fromEntries(SUPPORTED_FILE_TYPES.map((type) => [type.mimeType, [type.extension]])),
    maxFiles: maxFiles || 1,
  });

  const getFilesList = () => {
    if (multipleFiles && fileState.files) {
      return fileState.files.map((file, index) => (
        <CoreBox key={index} sx={sxStyles.uploadedFilesWrap}>
          <CoreTypography sx={sxStyles.uploadedFileTitle} onClick={() => handleDownload(file.data, file.name)}>
            {file.name}
          </CoreTypography>

          <IconButton color="secondary" data-testid={`remove-${file.name}`} onClick={() => onRemoveFile(index)}>
            <DeleteIcon2 />
          </IconButton>
        </CoreBox>
      ));
    }
    if (fileState.file) {
      return (
        <CoreBox sx={sxStyles.uploadedFilesWrap}>
          <CoreTypography
            sx={sxStyles.uploadedFileTitle}
            onClick={() => handleDownload(`${PDF_FILE_PREFIX}${fileState.file}`, filename || 'file.pdf')}
          >
            {fileState.uploadedFilename}
          </CoreTypography>

          <IconButton color="secondary" data-testid="remove-single-file" onClick={() => onRemoveFile(0)}>
            <DeleteIcon2 />
          </IconButton>
        </CoreBox>
      );
    }
  };

  return (
    <FormControl
      sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}
      error={!!error}
    >
      <CoreTypography variant="subtitle2" component="h4">
        {title}
      </CoreTypography>

      {filename ? (
        <CoreBox sx={{ display: 'flex', gap: '1.5rem', alignItems: 'center', alignSelf: 'stretch' }}>
          <CoreTypography variant="body2" sx={{ display: 'flex', flex: '1 0 0' }}>
            {filename}
          </CoreTypography>

          <CoreBox sx={{ display: 'flex', width: '11.375rem', gap: '0.5rem' }}>
            <ActionButton onClick={onRemoveAllFiles} variant="text" startIcon={<DeleteIcon />}>
              Remove
            </ActionButton>

            <ActionButton
              onClick={() =>
                setUIState((prev) => ({ ...prev, [multipleFiles ? 'isUploadOpen' : 'isPreviewOpen']: true }))
              }
              variant="outlined"
            >
              Preview
            </ActionButton>
          </CoreBox>
        </CoreBox>
      ) : (
        <ActionButton
          onClick={() => setUIState((prev) => ({ ...prev, isUploadOpen: true }))}
          variant="outlined"
          startIcon={<UploadIcon />}
        >
          Upload
        </ActionButton>
      )}

      <CoreDialog
        sx={sxStyles.dialogWrap}
        open={uiState.isUploadOpen}
        onClose={() => setUIState((prev) => ({ ...prev, isUploadOpen: false }))}
      >
        <CoreBox sx={sxStyles.dialogTitlesWrap}>
          <CoreTypography variant="subtitle1" component="h2">
            {title}
          </CoreTypography>

          {maxFiles ? <CoreTypography>{`Add up to ${maxFiles} files.`}</CoreTypography> : null}
        </CoreBox>

        {uiState.isShowAlert && !fileState.file ? (
          <CoreBox sx={sxStyles.alertWrap}>
            <StyledAlert severity="error">
              <AlertTitle>Unable to upload file</AlertTitle>
              Unable to download the file because it is encrypted or has restricted access. Please select a different
              file to proceed.
            </StyledAlert>
          </CoreBox>
        ) : null}

        <CoreBox sx={sxStyles.dragAndDropZoneWrap}>
          <StyledDragAndDropZone
            {...getRootProps({
              className: `dropzone${
                uiState.dragActive ? (uiState.isValidDrag ? ' drag-active-valid' : ' drag-active-invalid') : ''
              }`,
            })}
            style={{
              borderColor: uiState.dragActive ? (uiState.isValidDrag ? 'green' : 'red') : undefined,
            }}
          >
            <input data-testid="dropzone" {...getInputProps()} />

            {uiState.isLoading ? (
              <CoreBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <CoreTypography variant="body2">
                  {processingFiles.length > 1 ? 'Converting files to PDF...' : 'Converting file to PDF...'}
                </CoreTypography>
              </CoreBox>
            ) : (
              <>
                <UploadIcon2 style={{ pointerEvents: 'none', width: '30px', height: '30px' }} />
                <CoreTypography variant="subtitle1" component="h2">
                  Click to upload or drag to this area
                </CoreTypography>
                <CoreTypography sx={sxStyles.supportedFilesText} variant="body2">
                  {`Supported file types include .pdf, .doc, .docx, .rtf, or .txt. (max. ${MAX_SIZE_KB} KB)`}
                </CoreTypography>
              </>
            )}
          </StyledDragAndDropZone>

          {getFilesList()}

          <CoreTypography variant="caption">
            Only include letters and numbers in the file name. File will be converted to PDF if needed.
          </CoreTypography>
        </CoreBox>

        <DialogFooter
          goBackButtonTitle="Cancel"
          goNextButtonTitle="Review"
          onGoBack={handleOnCancel}
          onGoNext={() => setUIState((prev) => ({ ...prev, isPreviewOpen: true }))}
          isDisableNextButton={!fileState.file}
        />
      </CoreDialog>

      {error && <FormHelperText>{error}</FormHelperText>}

      {uiState.isPreviewOpen ? (
        <ReviewPdf
          title={previewTitle || 'File Preview'}
          subtitle="Please review the PDF preview of file uploaded. Scroll to the bottom if necessary to confirm and continue."
          downloadedFileName={filename}
          isOpen={uiState.isPreviewOpen}
          isLoading={false}
          reviewType={uiState.isUploadOpen ? ReviewTypes.PREVIEW_UPLOAD : ReviewTypes.PREVIEW_FORM}
          pdf={fileState.file}
          onClose={() => setUIState((prev) => ({ ...prev, isPreviewOpen: false }))}
          onContinue={handleOnContinue}
        />
      ) : null}
    </FormControl>
  );
};
